// @refresh reset
import React, { createContext, useState, useEffect } from "react";
import { useSession } from "~/shared/lib/auth/auth-provider";
import * as Sentry from "@sentry/react";
import { useAttachmentPolicy } from "~/entities/chat";
import { useIsAdmin } from "~/entities/admin";
import { useCurrentUser } from "~/entities/user";
import type { AttachmentPolicyDto } from "~/shared/types/chats";

interface User {
    id: string;
    firstName: string;
    surname: string;
    name: string;
    email: string;
    image: string | null;
}

interface UserContextType {
    user: User | null;
    attachmentPolicy: AttachmentPolicyDto | null;
    updateUser: (updatedUser: User) => void;
    isSuperAdmin: boolean;
    isSuperAdminLoading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { data: session } = useSession();
    const [user, setUser] = useState<User | null>(null);
    const [attachmentPolicy, setAttachmentPolicy] = useState<AttachmentPolicyDto | null>(null);

    const getUpdatedUser = useCurrentUser();
    const getAttachmentPolicy = useAttachmentPolicy();
    const getUserPolicy = useIsAdmin();

    useEffect(() => {
        if (getUpdatedUser.isSuccess && getUpdatedUser.data) {
            const userData = getUpdatedUser.data;

            const updatedUserData = {
                id: userData.id,
                firstName: userData.firstName ?? "",
                surname: userData.surname ?? "",
                name: userData.name ?? "",
                image: userData.image ?? "",
                email: userData.email,
            };

            setUser(updatedUserData);

            Sentry.setUser({
                id: userData.id,
                email: userData.email,
                username: userData.name ?? undefined,
            });
        } else if (!getUpdatedUser.data) {
            Sentry.setUser(null);
        }
    }, [getUpdatedUser.data, getUpdatedUser.isSuccess, session]);

    useEffect(() => {
        if (getAttachmentPolicy.isSuccess && getAttachmentPolicy.data) {
            setAttachmentPolicy(getAttachmentPolicy.data);
        }
    }, [getAttachmentPolicy.data, getAttachmentPolicy.isSuccess]);

    const isSuperAdmin = getUserPolicy.data ?? false;

    const updateUser = (updatedUser: User) => {
        setUser(updatedUser);
    };

    return (
        <UserContext.Provider
            value={{
                user,
                updateUser,
                attachmentPolicy,
                isSuperAdmin,
                isSuperAdminLoading: getUserPolicy.isLoading,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
